import { t, useLanguage } from "@/i18n/LanguageContext";import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, Eyebrow, DecorativeStripe } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ProjectMap } from "@/components/ProjectMap";
import { ProjectLoadStatus } from "@/components/ProjectLoadStatus";

export default function Lavori() {useLanguage();
  const { projects, projectsLoaded, projectsLoading, projectsError, content } = useSite();
  const [active, setActive] = useState("Tutti");
  const [selected, setSelected] = useState(null);

  const categories = useMemo(
    () => ["Tutti", ...new Set(projects.map((p) => p.category))],
    [projects]
  );
  const filtered = useMemo(() => active === "Tutti" ? projects : projects.filter((p) => p.category === active), [active, projects]);
  const activeCount = content.stats.find((stat) => stat.label === "Cantieri in essere")?.value;
  const startingCount = content.stats.find((stat) => stat.label === "Cantieri in partenza")?.value;

  return (
    <main data-testid="lavori-page">
      <section className="relative bg-[#F8FAFC] pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>{t("I nostri lavori")}</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight text-brand-navydeep sm:text-5xl lg:text-6xl" data-testid="works-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>{t("Cantieri che parlano da soli")}

              </motion.span>
            </span>
          </h1>
        </div>
        <DecorativeStripe testId="works-header-decorative-stripe" />
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-wrap gap-2" data-testid="project-filters">
            {t(categories.map((cat) =>
            <button
              key={cat}
              data-testid={`project-filter-${cat === "Tutti" ? "all" : cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setActive(cat)}
              aria-pressed={active === cat}
              className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all duration-300 ${
              active === cat ?
              "bg-brand-navy text-white" :
              "border border-slate-200 bg-white text-slate-600 hover:border-brand-lime hover:text-brand-navy"}`
              }>
              
                {t(cat)}
              </button>
            ))}
          </div>

          <ProjectLoadStatus />
          {projectsLoaded && <ProjectMap projects={filtered} onSelect={setSelected} />}

          <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
            <AnimatePresence mode="popLayout">
              {t(filtered.map((p) =>
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`project-card-${p.id}`}
                className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
                role="button"
                tabIndex={0}
                aria-label={t(p.title)}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelected(p); } }}
                onClick={() => setSelected(p)}>
                
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.image} alt={t(p.title)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span
                    data-testid={`project-status-${p.id}`}
                    className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${
                    p.status === "In corso" ? "bg-brand-lime text-brand-navydeep" : p.status === "Da realizzare" ? "bg-brand-amberlight text-brand-amberdark" : "bg-brand-navydeep text-white"}`
                    }>
                    
                      {t(p.status)}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-limedark">{t(p.category)}</p>
                    <h3 className="mt-2 font-display text-lg font-bold leading-snug text-brand-navydeep">{t(p.title)}</h3>
                    <p className="mt-3 flex flex-wrap items-start gap-x-4 gap-y-2 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{t(p.location)}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{t(p.year)}</span>
                    </p>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          {t(projectsLoaded && !projectsLoading && !projectsError && filtered.length === 0 &&
          <p className="py-16 text-center text-slate-500" data-testid="projects-empty">{t("Nessun lavoro in questa categoria.")}</p>)
          }
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="project-dialog" closeTestId="project-dialog-close" data-lenis-prevent className="max-h-[90vh] max-w-2xl overflow-y-auto p-0">
          {t(selected &&
          <div>
              <img src={selected.image} alt={t(selected.title)} className="h-64 w-full object-cover" />
              <div className="p-7">
                {t(selected.image_note && <p data-testid="project-dialog-image-note" className="mb-4 text-xs text-slate-500">{t(selected.image_note)}</p>)}
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-limedark">{t(selected.category)} · {t(selected.status)}</p>
                <DialogTitle data-testid="project-dialog-title" className="mt-2 font-display text-2xl font-bold text-brand-navydeep">{t(selected.title)}</DialogTitle>
                <p className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-2 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{t(selected.location)}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{t(selected.year)}</span>
                </p>
                <DialogDescription data-testid="project-dialog-description" className="mt-4 leading-relaxed text-slate-600">{t(selected.description)}</DialogDescription>
              </div>
            </div>)
          }
        </DialogContent>
      </Dialog>

      <Reveal className="bg-[#F8FAFC] py-16">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p data-testid="works-company-counts" className="font-display text-xl font-bold text-brand-navydeep">{t(`${activeCount} cantieri in essere, ${startingCount} in partenza — e tanti altri in arrivo.`)}</p>
        </div>
      </Reveal>
    </main>);

}
